import React from 'react';
import classNames from 'classnames';
import styles from './TableOfContents.module.css';
import Metadata from '../../MetadataPlugin/Metadata';
import type { HeadingElement } from '../types';
import type { AhaSlateEditor } from '../../../types/aha-editor';

const Heading: React.FC<{ heading: HeadingElement; showBorder?: boolean }> = ({
  heading,
  showBorder = true,
}) => {
  return (
    <ul
      className={classNames(styles.list, { [styles.showBorder]: showBorder })}
    >
      <li className={styles.listItem}>
        <a className={styles.link} href={`#${heading.id}`}>
          {heading.text}
        </a>
        {heading.children.map((childHeading, index) => (
          <Heading key={index} heading={childHeading} showBorder />
        ))}
      </li>
    </ul>
  );
};

interface TableOfContentsProps {
  headings: HeadingElement[];
  editor: AhaSlateEditor;
}
const TableOfContents: React.FC<TableOfContentsProps> = ({
  headings,
  editor,
}) => {
  const detailsProps: { open?: boolean } = {};
  const collapsed = editor.metadata?.tableOfContentsCollapse;
  if (!collapsed) {
    detailsProps.open = true;
  }

  const onToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const details = event.currentTarget;
    // Prevents toggling on first render
    if (!collapsed === details.open) return;
    Metadata.updateMetadata(editor, {
      key: 'tableOfContentsCollapse',
      value: !collapsed,
    });
  };
  return (
    <nav
      className={classNames(styles.container, 'aha-editor-toc')}
      aria-labelledby="toc-title"
    >
      <details className={styles.details} {...detailsProps} onToggle={onToggle}>
        <summary id="toc-title" className={classNames(styles.summary)}>
          <span>
            <i className="fa-regular fa-poll-h fa-fw" />
          </span>
          <span className={styles.label}>Table of contents</span>
          <span className={styles.expansionIcon}>
            <i className="fa-regular fa-angle-right fa-fw" />
          </span>
        </summary>
        {headings.length ? (
          headings.map((heading, index) => (
            <Heading key={index} heading={heading} showBorder={false} />
          ))
        ) : (
          <div>
            <aha-empty-state className={styles.emptyState}>
              Headings you add to your document will appear as sections here.
            </aha-empty-state>
          </div>
        )}
      </details>
    </nav>
  );
};

export default TableOfContents;
